/**
 * @file THE PERF GOVERNOR — the one decision "should this machine stop trying
 * for 60 and lock to 30?", and nothing else.
 *
 * Most laptops that will ever run this have no dedicated GPU. On integrated
 * silicon the city lands somewhere around 40 fps, and 40 fps is the worst
 * number there is: the frame budget is missed by a different amount every
 * frame, so the camera pans in a stutter that reads as a BUG rather than as a
 * slow machine. A locked 30 is measurably fewer frames and subjectively
 * smoother, which is the whole reason this module exists.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO IMPORTS. NOT THREE, NOT THE DOM, NOT A TIMER.
 * ─────────────────────────────────────────────────────────────────────────────
 * Same discipline as `systems/Driving.js`, `traffic/Traffic.js` and
 * `systems/Carjack.js`: everything comes in through the one argument, so the
 * entire policy is provable in node with a hand-written stream of numbers
 * (`tests/perf.test.mjs`). The caller owns the clock and owns the throttle;
 * this owns the JUDGEMENT. It never reads `performance.now()` itself — a module
 * that samples its own clock cannot be tested against a 200 ms spike without
 * actually stalling for 200 ms.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MEDIAN, NEVER MEAN
 * ─────────────────────────────────────────────────────────────────────────────
 * A mean is a trap here, and it is the trap that makes a good machine feel
 * punished. This engine has scheduled, unavoidable hitches: a chunk rebuild
 * when the camera crosses a cell, a GC pause, a texture upload, an impostor
 * bake. Ten 200 ms spikes in a 180-frame window drag a 16.7 ms mean up past
 * 27 ms — over the engage threshold — on a machine that is drawing 60 fps for
 * 170 of those 180 frames. The median moves by nothing at all, because ten
 * outliers cannot move the 90th element of a sorted list. The window is what
 * we are asking about ("is this machine typically slow?"), and the median is
 * the only summary that answers that question instead of a different one.
 *
 * Lower median on even counts (`Math.floor((n - 1) / 2)`) rather than the
 * average of the two middles, so the result is always a SAMPLE THAT ACTUALLY
 * HAPPENED and the function is exactly reproducible in a test.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LATCH IS DELIBERATE — DO NOT ADD AUTOMATIC RELEASE
 * ─────────────────────────────────────────────────────────────────────────────
 * Once the cap engages, only `reset()` releases it. This looks like a missing
 * feature and it is a closed decision: WHILE CAPPED AT 30 THE OBSERVED FRAME
 * INTERVAL IS ~33 ms BY CONSTRUCTION. That 33 ms is the cap's own doing, not a
 * measurement of the machine, so it carries zero information about whether 60
 * is achievable. Any release rule reading it would fire on the cap itself:
 * release → the real 25 ms interval comes back → re-engage → 33 ms → release,
 * a visible oscillation between two frame rates, which is strictly worse than
 * either. Measuring headroom honestly would mean un-capping for a probe window
 * and eating the jitter to find out — that is a bigger feature than this one,
 * and nobody has asked for it. The level loader calls `reset()`; that is the
 * release path.
 *
 * For the same reason a capped governor STOPS FEEDING THE WINDOW (rule 7): the
 * samples can no longer change any decision, so pushing them would only make
 * `stats().median` lie about the machine by reporting the cap back to you.
 * `stats()` keeps reporting the last honest window instead.
 */

/**
 * @typedef {object} PerfStats
 * @property {number} median  Current window median in ms; `0` when the window is empty.
 * @property {number} samples How many samples the window currently holds.
 * @property {number} warmup  How many warmup samples REMAIN to be discarded.
 * @property {boolean} capped Whether the cap has latched.
 */

/**
 * @typedef {object} PerfGovernor
 * @property {(intervalMs: number) => number} sample Feed one wall-clock frame interval in ms; returns the cap in Hz (`0` = uncapped).
 * @property {() => number} cap Current cap in Hz, `0` = uncapped.
 * @property {() => void} reset Clear the window, restart warmup, release the cap.
 * @property {() => PerfStats} stats Truthful read-out of the window.
 */

/**
 * @typedef {object} PerfGovernorOptions
 * @property {number} [capHz=30] The cap to engage, in Hz.
 * @property {number} [engageMs=20] A window median ABOVE this engages the cap (20 ms is just under 50 fps).
 * @property {number} [windowFrames=180] Rolling window size (~3 s at 60 fps).
 * @property {number} [warmupFrames=180] Valid samples discarded before anything is judged.
 * @property {number} [maxSampleMs=250] Samples above this are garbage (hidden tab, level build) and are rejected outright.
 */

/**
 * Build a frame-pacing governor.
 *
 * The caller feeds it real frame intervals and does whatever it is told:
 * `0` means present every frame, anything else is a target in Hz.
 *
 * @param {PerfGovernorOptions} [opts]
 * @returns {PerfGovernor}
 */
export function createPerfGovernor(opts = {}) {
  const capHz = opts.capHz ?? 30;
  const engageMs = opts.engageMs ?? 20;
  const windowFrames = Math.max(1, Math.floor(opts.windowFrames ?? 180));
  const warmupFrames = Math.max(0, Math.floor(opts.warmupFrames ?? 180));
  const maxSampleMs = opts.maxSampleMs ?? 250;

  /** Ring buffer of the last `windowFrames` valid post-warmup intervals, ms. */
  const ring = new Float64Array(windowFrames);
  /** Scratch for the median so a per-frame decision allocates nothing. */
  const sorted = new Float64Array(windowFrames);

  let head = 0;
  let count = 0;
  let warmup = warmupFrames;
  let capped = false;

  /** Lower median of the live window, in ms. `0` when empty. @returns {number} */
  function median() {
    if (count === 0) return 0;
    for (let i = 0; i < count; i++) sorted[i] = ring[i];
    // Insertion sort: `count` is 180 and the array is nearly always already
    // near-sorted junk-free frame times, and this beats allocating a subarray
    // and a comparator every frame. Ascending, total order, no NaN can be here
    // (the validity gate rejected them), so the result is deterministic.
    for (let i = 1; i < count; i++) {
      const v = sorted[i];
      let j = i - 1;
      while (j >= 0 && sorted[j] > v) {
        sorted[j + 1] = sorted[j];
        j--;
      }
      sorted[j + 1] = v;
    }
    return sorted[Math.floor((count - 1) / 2)];
  }

  return {
    sample(intervalMs) {
      // (1) Validity. A rejected sample is as if the frame never happened: it
      // does not enter the window AND it does not spend warmup. A hidden tab
      // that returns one 4000 ms interval must not be able to buy its way
      // through warmup with garbage.
      if (typeof intervalMs !== 'number' || !Number.isFinite(intervalMs)) return capped ? capHz : 0;
      if (intervalMs <= 0 || intervalMs > maxSampleMs) return capped ? capHz : 0;

      // (7) Latched: the window can no longer change anything, so leave it
      //     holding the last honest measurement rather than the cap's own beat.
      if (capped) return capHz;

      // (2) Warmup. Level build, shader compilation and texture upload all land
      //     here and none of them describe the machine's steady state.
      if (warmup > 0) {
        warmup--;
        return 0;
      }

      // (3) Into the ring.
      ring[head] = intervalMs;
      head = (head + 1) % windowFrames;
      if (count < windowFrames) count++;

      // (4) A partial window is not evidence. Judge only a full one.
      if (count === windowFrames && median() > engageMs) capped = true;

      return capped ? capHz : 0;
    },

    cap() {
      return capped ? capHz : 0;
    },

    reset() {
      head = 0;
      count = 0;
      warmup = warmupFrames;
      capped = false;
    },

    stats() {
      return { median: median(), samples: count, warmup, capped };
    },
  };
}
